import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SelectDropdownProps {
  label?: string;
  value?: string | number;
  onChange: (value: string | number | undefined) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  emptyText?: string;
  className?: string;
  getOptionLabel?: (option: SelectOption) => string; // 用于自定义显示标签
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = '请选择...',
  disabled = false,
  loading = false,
  emptyText = '暂无数据',
  className = '',
  getOptionLabel,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // 计算下拉菜单位置
  useEffect(() => {
    if (!showDropdown || !containerRef.current) return;

    const updatePosition = () => {
      const container = containerRef.current;
      if (!container) return;

      // 获取下拉框输入区域的元素（实际可点击的区域）
      const inputElement = container.querySelector('div[class*="cursor-pointer"]') as HTMLElement;
      const targetElement = inputElement || container;

      if (!targetElement) return;

      const rect = targetElement.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4, // 使用 getBoundingClientRect 时不需要加 scrollY，因为 fixed 定位是相对于视口的
        left: rect.left,
        width: rect.width,
      });
    };

    // 使用 requestAnimationFrame 确保在 DOM 更新后计算位置
    const rafId = requestAnimationFrame(() => {
      updatePosition();
    });

    // 监听滚动和窗口大小变化
    const handleScroll = () => {
      updatePosition();
    };
    const handleResize = () => {
      updatePosition();
    };

    // 监听所有滚动事件（包括父容器的滚动）
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [showDropdown]);

  // 点击外部关闭下拉框
  useEffect(() => {
    if (!showDropdown) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setShowDropdown(false);
      }
    };

    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // 获取当前选中项的标签
  const selectedOption = options.find((opt) => {
    // 处理 value 可能是 undefined、空字符串或数字的情况
    if (value === undefined || value === null) return false;
    return String(opt.value) === String(value);
  });
  const displayText = selectedOption
    ? getOptionLabel
      ? getOptionLabel(selectedOption)
      : selectedOption.label
    : placeholder;

  return (
    <div className={`relative ${className || 'mb-4'}`} ref={containerRef} style={{ overflow: 'visible' }}>
      {label && <label className="block text-white/70 text-sm mb-2">{label}</label>}
      <div className="relative" style={{ overflow: 'visible', position: 'relative' }}>
        {/* 下拉框输入区域 */}
        <div
          onClick={(e) => {
            if (!disabled && !loading) {
              e.stopPropagation();
              setShowDropdown(!showDropdown);
            }
          }}
          className={`w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/40 flex items-center justify-between min-w-0 ${
            disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-white/15'
          }`}
        >
          <span className={`${value ? 'text-white' : 'text-white/50'} truncate flex-1 min-w-0`}>{displayText}</span>
          <svg
            className={`w-5 h-5 text-white/60 transition-transform flex-shrink-0 ml-2 ${
              showDropdown ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* 下拉列表 - 使用 Portal 渲染到 body */}
        {showDropdown &&
          !disabled &&
          !loading &&
          createPortal(
            <div
              ref={dropdownRef}
              className="fixed bg-gray-800 border border-white/20 rounded-lg max-h-60 overflow-y-auto shadow-lg"
              style={{
                position: 'fixed',
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
                width: `${dropdownPosition.width}px`,
                zIndex: 9999,
                minHeight: '40px',
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {loading ? (
                <div className="px-3 py-2 text-white/50 text-sm text-center">加载中...</div>
              ) : options.length === 0 ? (
                <div className="px-3 py-2 text-white/50 text-sm text-center">{emptyText}</div>
              ) : (
                options.map((option) => {
                  // 处理 value 可能是 undefined、空字符串或数字的情况
                  const isSelected = value !== undefined && value !== null && String(option.value) === String(value);
                  const displayLabel = getOptionLabel ? getOptionLabel(option) : option.label;
                  return (
                    <div
                      key={String(option.value)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(option.value);
                        setShowDropdown(false);
                      }}
                      className={`px-3 py-2 cursor-pointer text-sm flex items-center justify-between ${
                        isSelected ? 'bg-green-500/20 text-green-200' : 'hover:bg-white/10 text-white'
                      }`}
                    >
                      <span>{displayLabel}</span>
                      {isSelected && (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  );
                })
              )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default SelectDropdown;
